/**
 * Meeting Recorder Component
 * In-browser audio/video recording for sales meetings using MediaRecorder API.
 * Records locally and provides the blob for upload to the transcription pipeline.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Mic,
  Video,
  Square,
  Play,
  Pause,
  Trash2,
  Upload,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

export interface RecordingResult {
  blob: Blob;
  duration: number;
  mimeType: string;
  mode: 'audio' | 'video';
}

interface MeetingRecorderProps {
  onRecordingComplete: (result: RecordingResult) => void;
  onCancel?: () => void;
  defaultMode?: 'audio' | 'video';
}

function formatTime(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const MeetingRecorder: React.FC<MeetingRecorderProps> = ({
  onRecordingComplete,
  onCancel,
  defaultMode = 'audio',
}) => {
  const [mode, setMode] = useState<'audio' | 'video'>(defaultMode);
  const [status, setStatus] = useState<'idle' | 'recording' | 'paused' | 'stopped'>('idle');
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);
  const startTimeRef = useRef<number>(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, []);

  // Attach live stream to video element once recording starts and DOM updates
  useEffect(() => {
    if (mode === 'video' && (status === 'recording' || status === 'paused') && streamRef.current && videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = streamRef.current;
      videoPreviewRef.current.play().catch(() => {});
    }
  }, [mode, status]);

  const startRecording = useCallback(async () => {
    setError(null);
    try {
      const constraints: MediaStreamConstraints = mode === 'video'
        ? { audio: true, video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } } }
        : { audio: true };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const mimeType = mode === 'video'
        ? (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ? 'video/webm;codecs=vp9,opus' : 'video/webm')
        : (MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm');

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordingBlob(blob);
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };

      recorder.start(1000); // Collect data every second
      startTimeRef.current = Date.now();
      setStatus('recording');
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);

    } catch (err: any) {
      console.error('Failed to start recording:', err);
      if (err.name === 'NotAllowedError') {
        setError('Microphone/camera access denied. Please allow permissions in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setError(`No ${mode === 'video' ? 'camera' : 'microphone'} found. Please check your device.`);
      } else {
        setError(`Failed to start recording: ${err.message}`);
      }
    }
  }, [mode]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && status === 'recording') {
      mediaRecorderRef.current.pause();
      if (timerRef.current) clearInterval(timerRef.current);
      setStatus('paused');
    }
  }, [status]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && status === 'paused') {
      mediaRecorderRef.current.resume();
      const pausedTime = recordingTime;
      const resumeStart = Date.now();
      timerRef.current = setInterval(() => {
        setRecordingTime(pausedTime + Math.floor((Date.now() - resumeStart) / 1000));
      }, 1000);
      setStatus('recording');
    }
  }, [status, recordingTime]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && (status === 'recording' || status === 'paused')) {
      mediaRecorderRef.current.stop();
      if (timerRef.current) clearInterval(timerRef.current);
      setStatus('stopped');
    }
  }, [status]);

  const discardRecording = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setRecordingBlob(null);
    setPreviewUrl(null);
    setStatus('idle');
    setRecordingTime(0);
    chunksRef.current = [];
  }, [previewUrl]);

  const submitRecording = useCallback(() => {
    if (recordingBlob) {
      onRecordingComplete({
        blob: recordingBlob,
        duration: recordingTime,
        mimeType: recordingBlob.type,
        mode,
      });
    }
  }, [recordingBlob, recordingTime, mode, onRecordingComplete]);

  return (
    <div className="space-y-3">
      {/* Mode Selector - only when idle */}
      {status === 'idle' && !recordingBlob && (
        <div className="flex gap-2">
          <button
            onClick={() => setMode('audio')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
              mode === 'audio'
                ? 'border-purple-500 bg-purple-50 text-purple-700'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <Mic className="w-4 h-4" />
            Audio Only
          </button>
          <button
            onClick={() => setMode('video')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
              mode === 'video'
                ? 'border-purple-500 bg-purple-50 text-purple-700'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <Video className="w-4 h-4" />
            Audio + Video
          </button>
        </div>
      )}

      {/* Video Preview — always render in video mode so ref is available for stream attachment */}
      {mode === 'video' && status !== 'stopped' && (
        <div className={`rounded-lg overflow-hidden bg-black aspect-video ${status === 'idle' ? 'hidden' : ''}`}>
          <video
            ref={videoPreviewRef}
            muted
            playsInline
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Recording Controls */}
      <div className="flex items-center gap-2">
        {status === 'idle' && !recordingBlob && (
          <button
            onClick={startRecording}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
          >
            {mode === 'video' ? <Video className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            Start Recording
          </button>
        )}

        {(status === 'recording' || status === 'paused') && (
          <>
            <button
              onClick={stopRecording}
              className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm"
            >
              <Square className="w-3.5 h-3.5" />
              Stop
            </button>
            {status === 'recording' ? (
              <button
                onClick={pauseRecording}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                <Pause className="w-3.5 h-3.5" />
                Pause
              </button>
            ) : (
              <button
                onClick={resumeRecording}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                <Play className="w-3.5 h-3.5" />
                Resume
              </button>
            )}
            <div className="flex items-center gap-1.5 text-sm font-mono">
              <span className={`w-2 h-2 rounded-full ${status === 'recording' ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'}`} />
              <span className="text-gray-700">{formatTime(recordingTime)}</span>
            </div>
          </>
        )}

        {onCancel && status === 'idle' && !recordingBlob && (
          <button
            onClick={onCancel}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Completed Recording Preview */}
      {status === 'stopped' && recordingBlob && (
        <div className="border rounded-lg p-3 bg-gray-50 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium text-gray-900">
                Recording Complete
              </span>
              <span className="text-xs text-gray-500">
                {formatTime(recordingTime)} &bull; {formatFileSize(recordingBlob.size)}
              </span>
            </div>
          </div>

          {/* Audio/Video playback */}
          {previewUrl && (
            mode === 'video' ? (
              <video
                src={previewUrl}
                controls
                className="w-full rounded-lg max-h-48"
              />
            ) : (
              <audio src={previewUrl} controls className="w-full" />
            )
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={submitRecording}
              className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
            >
              <Upload className="w-3.5 h-3.5" />
              Upload & Transcribe
            </button>
            <button
              onClick={discardRecording}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-600"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Discard
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-2 rounded">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
};
